import {
  CheckCircle2,
  CircleAlert,
  Clock3,
  Download,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/atoms/button";
import { HistoryExportStatus } from "@/hooks/useOrderHistoryExport";

export function HistoryExportPanel({
  job,
  error,
  downloading,
  onDownload,
  onRetry,
  onDismiss,
  canRetry = true,
}: {
  job: HistoryExportStatus | null;
  error: string | null;
  downloading: boolean;
  onDownload: () => void;
  onRetry: () => void;
  onDismiss: () => void;
  canRetry?: boolean;
}) {
  if (!job && !error) return null;
  const active = job?.status === "pending" || job?.status === "generating";
  const progress = Math.max(0, Math.min(100, job?.progress || 0));
  const hasError = job?.status === "error" || Boolean(error);
  const variant = hasError
    ? {
        container: "border-red-200 bg-red-50",
        text: "text-red-950",
        filename: "text-red-800",
        Icon: CircleAlert,
      }
    : job?.status === "ready"
      ? {
          container: "border-emerald-200 bg-emerald-50",
          text: "text-emerald-950",
          filename: "text-emerald-800",
          Icon: CheckCircle2,
        }
      : {
          container: "border-indigo-200 bg-indigo-50",
          text: "text-indigo-950",
          filename: "text-indigo-800",
          Icon: Clock3,
        };
  const StatusIcon = variant.Icon;

  return (
    <div
      data-testid="history-export-panel"
      className={`mb-4 space-y-3 rounded-lg border p-4 ${variant.container}`}
    >
      <div
        role={hasError ? "alert" : "status"}
        aria-live={hasError ? "assertive" : "polite"}
        className={`flex items-start gap-3 text-sm ${variant.text}`}
      >
        <StatusIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          {job?.status === "pending" && (
            <p>Exportación pendiente. Puedes seguir usando NEU.</p>
          )}
          {job?.status === "generating" && (
            <p>Generando CSV: {progress}%. Puedes seguir usando NEU.</p>
          )}
          {job?.status === "ready" && (
            <p>
              CSV listo: {job.total ?? 0} órdenes. Disponible durante 24 horas
              desde la solicitud.
            </p>
          )}
          {job?.status === "error" && (
            <p>
              {job.message ||
                "No se pudo generar el CSV. Puedes solicitar una nueva exportación."}
            </p>
          )}
          {error && canRetry && <p>{error}</p>}
        </div>
      </div>
      {active && (
        <progress
          className="w-full h-2 accent-indigo-600"
          aria-label="Progreso de exportación CSV"
          max={100}
          value={progress}
        />
      )}
      {job?.filename && (
        <p className={`break-all text-xs ${variant.filename}`}>
          {job.filename}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {job?.status === "ready" && (
          <Button type="button" onClick={onDownload} disabled={downloading}>
            {downloading ? (
              <Loader2 className="animate-spin" aria-hidden="true" />
            ) : (
              <Download aria-hidden="true" />
            )}
            {downloading ? "Descargando..." : "Descargar CSV"}
          </Button>
        )}
        {error && (
          <Button type="button" variant="outline" onClick={onRetry}>
            Consultar estado nuevamente
          </Button>
        )}
        {(!active || error) && (
          <Button type="button" variant="ghost" onClick={onDismiss}>
            Cerrar aviso
          </Button>
        )}
      </div>
    </div>
  );
}
